import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { getArticleUriFromLink } from '../../../util';
import { selectArticle } from '../../common/selectors/helpCenter';
import { appRoutes } from '../../routes';

/**
 * Creates a click listener that navigates to an article or category page by clicked link href.
 */
export const useNavigateToArticleClickListener = () => {
  const currentArticle = useSelector(selectArticle);
  const navigate = useNavigate();

  const navigateToArticle = useCallback(
    (isOtherArticle: boolean, articleUri: string, hash?: string) => {
      const pathname = appRoutes.articleByUri({ articleUri }) || undefined;

      navigate(
        {
          pathname,
          hash,
        },
        {
          replace: !isOtherArticle, // if the same article - it's scroll to article's anchor. Do not push to the history in this case.
        },
      );
    },
    [navigate],
  );

  return useCallback(
    (e: MouseEvent) => {
      if (e.target === null || !(e.target instanceof Element)) {
        return;
      }

      const href = e.target?.getAttribute('href');

      if (!href) {
        return;
      }

      const articleUri = getArticleUriFromLink(href);

      if (articleUri) {
        e.preventDefault();

        const [linkWithoutHash, hash] = href.split('#');
        const isOtherArticle = !currentArticle || linkWithoutHash !== encodeURI(currentArticle.url);

        navigateToArticle(isOtherArticle, articleUri, hash);

        return;
      }
    },
    [navigateToArticle, currentArticle],
  );
};
